import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { CheckCircle2, XCircle } from 'lucide-react';
interface SlideQuizProps {
  headline: string;
  options: string[];
  correctIndex: number;
  feedback: string;
  buttonLabel: string;
  onContinue: () => void;
  footer?: string;
  visualHint?: React.ReactNode;
}
export function SlideQuiz({
  headline,
  options,
  correctIndex,
  feedback,
  buttonLabel,
  onContinue,
  footer,
  visualHint
}: SlideQuizProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const hasAnswered = selectedIndex !== null;
  return <div className="h-full w-full flex flex-col px-12 py-16 md:px-24 md:py-20 max-w-7xl mx-auto" data-id="element-346">
      <div className="flex-1 flex flex-col justify-center" data-id="element-347">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-348">
          {/* Left Column: Question & Options */}
          <div className="space-y-10" data-id="element-349">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-white" data-id="element-350">
              {headline}
            </motion.h2>

            <div className="space-y-4" data-id="element-351">
              {options.map((option, idx) => {
              const isSelected = selectedIndex === idx;
              const isCorrect = idx === correctIndex;
              const showStatus = hasAnswered && (isSelected || isCorrect);
              let borderClass = 'border-navy-600 hover:border-teal-400/50 hover:bg-navy-700/50';
              if (hasAnswered) {
                if (isCorrect) borderClass = 'border-teal-400 bg-teal-400/10';else if (isSelected && !isCorrect) borderClass = 'border-red-500/50 bg-red-500/10 text-white/50';else borderClass = 'border-navy-700 opacity-50';
              }
              return <motion.button key={idx} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: idx * 0.1 + 0.2
              }} onClick={() => !hasAnswered && setSelectedIndex(idx)} disabled={hasAnswered} className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-300 flex items-center justify-between ${borderClass}`} data-id="element-352">
                    <span className="text-lg md:text-xl font-medium" data-id="element-353">
                      {option}
                    </span>
                    {showStatus && (isCorrect ? <CheckCircle2 className="text-teal-400 w-6 h-6" data-id="element-354" /> : <XCircle className="text-red-400 w-6 h-6" data-id="element-355" />)}
                  </motion.button>;
            })}
            </div>
          </div>

          {/* Right Column: Feedback & Visuals */}
          <div className="h-full flex flex-col justify-center min-h-[300px]" data-id="element-356">
            <AnimatePresence mode="wait" data-id="element-357">
              {!hasAnswered && visualHint ? <motion.div key="visual" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="w-full" data-id="element-358">
                  {visualHint}
                </motion.div> : hasAnswered ? <motion.div key="feedback" initial={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} className="bg-navy-700/50 border border-navy-600 p-8 md:p-10 rounded-2xl backdrop-blur-sm" data-id="element-359">
                  <div className="text-teal-400 font-medium mb-4 flex items-center gap-2" data-id="element-360">
                    <CheckCircle2 className="w-5 h-5" data-id="element-361" />
                    Insight
                  </div>
                  <p className="text-xl md:text-2xl leading-relaxed text-white/90 mb-8" data-id="element-362">
                    {feedback.split('→').map((part, i) => <span key={i} className="block mb-2" data-id="element-363">
                        {i > 0 && <span className="text-teal-400 mr-2" data-id="element-364">→</span>}
                        {part.trim()}
                      </span>)}
                  </p>
                  <Button onClick={onContinue} data-id="element-365">{buttonLabel}</Button>
                </motion.div> : null}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {footer && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1
    }} className="mt-auto pt-8 text-sm text-white/40 font-medium tracking-wide" data-id="element-366">
          {footer}
        </motion.div>}
    </div>;
}